// client/src/components/services_tester/RestApiTab.jsx
import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send, Security } from '@mui/icons-material';
import { DIRECT_SERVICES, HTTP_METHODS } from './common/constants';

export default function RestApiTab({
  isAuthenticated,
  selectedService,
  restPath,
  restMethod,
  restBody,
  restResponse,
  restLoading,
  setSelectedService,
  setRestPath,
  setRestMethod,
  setRestBody,
  handleSendRestRequest,
  isSendRequestDisabled,
  selectedServiceRequiresAuth,
  isValidJson,
}) {
  const methods = Array.isArray(HTTP_METHODS)
    ? HTTP_METHODS
    : Object.values(HTTP_METHODS || {});

  return (
    <Box
      sx={{
        p: 0.5,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      {!isAuthenticated && (
        <Alert
          severity="warning"
          icon={<Security fontSize="small" />}
          sx={{ mb: 0.75, py: 0.25, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0 } }}
        >
          Для защищённых сервисов требуется аутентификация
        </Alert>
      )}

      <Stack spacing={0.75} sx={{ flex: 1, minHeight: 0 }}>
        {/* Form */}
        <Grid container spacing={0.75} sx={{ flexShrink: 0 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Сервис</InputLabel>
              <Select
                value={selectedService || ''}
                onChange={(e) => setSelectedService(e.target.value)}
                label="Сервис"
                sx={{ fontSize: '0.75rem' }}
              >
                {DIRECT_SERVICES.map((s) => (
                  <MenuItem
                    key={s.key}
                    value={s.key}
                    disabled={s.requiresAuth && !isAuthenticated}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <span>{s.name}</span>
                      {s.requiresAuth && (
                        <Security
                          sx={{ fontSize: 14 }}
                          color={isAuthenticated ? 'success' : 'warning'}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Метод</InputLabel>
              <Select
                value={restMethod || 'GET'}
                onChange={(e) => setRestMethod(e.target.value)}
                label="Метод"
                sx={{ fontSize: '0.75rem' }}
              >
                {methods.map((m) => (
                  <MenuItem key={m} value={m} sx={{ fontSize: '0.75rem' }}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Путь запроса"
              value={restPath || ''}
              onChange={(e) => setRestPath(e.target.value)}
              placeholder="/api/health"
              sx={{
                '& input': { fontSize: '0.75rem' },
              }}
            />
          </Grid>
        </Grid>

        {restMethod !== 'GET' && (
          <TextField
            fullWidth
            size="small"
            label="Тело запроса (JSON)"
            multiline
            minRows={3}
            maxRows={8}
            value={restBody || ''}
            onChange={(e) => setRestBody(e.target.value)}
            placeholder='{"key": "value"}'
            error={Boolean(restBody && !isValidJson(restBody))}
            helperText={
              restBody && !isValidJson(restBody) ? 'Невалидный JSON' : undefined
            }
            sx={{
              '& textarea': { fontFamily: 'monospace', fontSize: '0.7rem' },
              '& .MuiFormHelperText-root': { fontSize: '0.6rem', mt: 0.25 },
            }}
          />
        )}

        <Box sx={{ mt: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSendRestRequest}
            disabled={
              isSendRequestDisabled ||
              (selectedServiceRequiresAuth && !isAuthenticated)
            }
            fullWidth
            startIcon={
              restLoading ? <CircularProgress size={14} color="inherit" /> : <Send fontSize="small" />
            }
            sx={{ fontSize: '0.7rem', py: 0.25 }}
          >
            {restLoading
              ? 'Отправка…'
              : selectedServiceRequiresAuth && !isAuthenticated
              ? 'Требуется аутентификация'
              : `Запрос к ${selectedService || '—'}`}
          </Button>
        </Box>

        {restResponse && (
          <Card variant="outlined" sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 } }}>
              <Typography
                variant="caption"
                sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5 }}
              >
                Ответ сервера
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 0.75,
                  bgcolor: 'grey.50',
                  maxHeight: 200,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.65rem',
                  lineHeight: 1.3,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {typeof restResponse === 'string'
                  ? restResponse
                  : JSON.stringify(restResponse, null, 2)}
              </Paper>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
}
